import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Crown, Loader2, Search, Shield, ShieldCheck, Star, User2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Admin user list — search + paginated. Read-only for now (edit / delete
// endpoints exist on the backend but not exposed here yet to keep the
// blast radius small; add them in a follow-up if needed).
export default function AdminUsers() {
  const { api } = useAuth();
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/admin/users", {
        params: { search: search.trim() || undefined, limit: LIMIT, offset },
      });
      const payload = Array.isArray(data) ? { items: data, total: data.length } : data;
      setItems(payload.items || []);
      setTotal(payload.total ?? (payload.items || []).length);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load users.");
    }
  }, [api, search, offset]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  useEffect(() => { setOffset(0); }, [search]);

  return (
    <div className="flex flex-col gap-5" data-testid="admin-users-page">
      <div>
        <Link to="/admin" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: "var(--brand)" }} data-testid="admin-users-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Admin
        </Link>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ color: "var(--on-surface)" }}>
          Users {total > 0 && <span className="text-base font-bold" style={{ color: "var(--on-surface-muted)" }}>· {total}</span>}
        </h1>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or nickname…"
          className="w-full pl-11 pr-4 py-3 rounded-2xl border text-base"
          style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
          data-testid="admin-users-search"
        />
      </div>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}
      {!items && <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
      </div>}
      {items && items.length === 0 && (
        <div className="card-elev p-8 text-center" data-testid="admin-users-empty">
          No users match your search.
        </div>
      )}
      {items && items.length > 0 && (
        <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-c)" }} data-testid="admin-users-list">
          {items.map((u) => <UserRow key={u.user_id} u={u} />)}
        </div>
      )}

      {(offset > 0 || (items && items.length === LIMIT)) && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={offset === 0}
            className="btn btn-light disabled:opacity-40"
            data-testid="admin-users-prev"
          >
            ← Prev
          </button>
          <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>
            {offset + 1}–{offset + (items?.length || 0)} of {total}
          </div>
          <button
            onClick={() => setOffset(offset + LIMIT)}
            disabled={!items || items.length < LIMIT}
            className="btn btn-light disabled:opacity-40"
            data-testid="admin-users-next"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function UserRow({ u }) {
  const roles = u.roles || [];
  return (
    <div className="py-3 flex items-center gap-3" data-testid={`admin-user-${u.user_id}`}>
      {u.avatar_data ? (
        <img src={u.avatar_data} alt="" className="w-10 h-10 rounded-full object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold"
             style={{ background: "var(--brand)", color: "white" }}>
          {(u.name?.[0] || "?").toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold truncate" style={{ color: "var(--on-surface)" }}>
          {u.name || "—"} {u.myap_id && <span className="text-[10px] font-mono ml-2" style={{ color: "var(--on-surface-muted)" }}>{u.myap_id}</span>}
        </div>
        <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>{u.email}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end max-w-[220px]">
        {roles.map((r) => (
          <span key={r}
                className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{
                  background: r === "admin" ? "color-mix(in srgb, var(--premium) 15%, transparent)"
                            : "color-mix(in srgb, var(--brand) 10%, transparent)",
                  color: r === "admin" ? "var(--premium)" : "var(--brand)",
                }}>
            {r === "admin" ? <Shield className="w-2.5 h-2.5" /> : r === "campsite_owner" || r === "owner" ? <Star className="w-2.5 h-2.5" /> : <User2 className="w-2.5 h-2.5" />}
            {r}
          </span>
        ))}
        {u.is_premium && (
          <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ background: "color-mix(in srgb, var(--premium) 12%, transparent)", color: "var(--premium)" }}>
            <Crown className="w-2.5 h-2.5" /> Premium
          </span>
        )}
      </div>
    </div>
  );
}
